import React, {useMemo, useRef} from 'react';
import {useTranslation} from 'next-i18next';
import {useRouter} from 'next/router';
import {Button, useDisclosure} from '@tempo/core';
import {useToast} from '@deezer/next/shared/hooks/useToast';
import DeleteClaimModal from '@deezer/next/features/ClaimAccess/components/DeleteClaimModal';
import type {Claim} from '@deezer/next/services/api/Starmania/requests/claim/types';
import {ClaimStatus} from '@deezer/next/services/api/Starmania/requests/claim/types';
import {
	isArtistManager,
	isLabelGroup,
	isUserApproved,
} from '@deezer/next/shared/utils/user';
import {Routes} from '@constants/routes';
import {
	useClaims,
	useUser,
	useStateDispatch,
} from '@deezer/next/contexts/main/hooks';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {setClaims} from '@deezer/next/contexts/main/actions';
import useScreenViewEvent from '@deezer/next/shared/hooks/useScreenViewEvent';
import {ScreenViewName} from '@deezer/next/services/tracking/LogCenter/events/types';
import NavigationLayout from '@deezer/next/shared/layouts/NavigationLayout';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import ClaimTable from '@deezer/next/features/ClaimAccess/components/ClaimTable';
import Placeholder from '@deezer/next/components/Placeholder';
import {getServerSideTranslationsWrapper} from '@deezer/next/shared/utils/next';

const RequestMonitoring = () => {
	const {t} = useTranslation('d4c');
	const claims = useClaims();
	const user = useUser();
	const {isOpen, onClose, onOpen} = useDisclosure();
	const selectedClaim = useRef<number>(undefined);
	const router = useRouter();
	const toast = useToast();
	const dispatch = useStateDispatch();
	const isClaimButtonVisible = useMemo(() => {
		return (
			claims.length === 0 ||
			claims.every(
				claim =>
					claim.status === ClaimStatus.DENIED ||
					claim.status === ClaimStatus.DELETED ||
					// Ensure approved artist manager and label group can create claim if
					// all claims are denied, deleted or also approved
					(isUserApproved(user) &&
						(isArtistManager(user) || isLabelGroup(user)) &&
						claim.status === ClaimStatus.APPROVED),
			)
		);
	}, [claims, user]);

	useScreenViewEvent(
		() => ({
			screenName: ScreenViewName.REQUEST_MONITORING,
		}),
		[],
	);

	const handleStartNewRequest = () => {
		router.push(Routes.CLAIM);
	};

	const handleClaimDeleteClick = (claim: Claim) => {
		selectedClaim.current = claim.id;
		onOpen();
	};
	const handleClaimDeleteModalClose = () => {
		onClose();
		selectedClaim.current = undefined;
	};
	const handleClaimDelete = async () => {
		if (!selectedClaim.current) return;
		try {
			await StarmaniaApi.claim.denyClaims(selectedClaim.current);
			const {data: updatedClaims} = await StarmaniaApi.claim.getClaims();
			dispatch(setClaims(updatedClaims));
			selectedClaim.current = undefined;
		} catch {
			toast({
				title: t('errormessage_text_errorwhilecancellingaccess_d4c-web'),
				description: t(
					'requestmonitoring_text_refreshpageorcontactcustservice_d4c-web',
				),
				status: 'error',
			});
		} finally {
			onClose();
		}
	};

	return (
		<PageLayout
			data-testid="request-monitoring"
			header={
				<TitleRow
					title={t('requestmonitoring_title_requestmonitoring_d4c-web')}
				/>
			}
			footer={
				isClaimButtonVisible && (
					<FooterRow
						rightElements={
							<Button
								data-testid="new-request-button"
								onClick={handleStartNewRequest}
								size="small"
							>
								{t('requestmonitoring_action_startnewrequest_d4c-web')}
							</Button>
						}
					/>
				)
			}
		>
			{claims.length > 0 ? (
				<ClaimTable claims={claims} onRemove={handleClaimDeleteClick} />
			) : (
				<Placeholder data-testid="request-monitoring-placeholder" />
			)}

			<DeleteClaimModal
				isOpen={isOpen}
				onClose={handleClaimDeleteModalClose}
				onDelete={handleClaimDelete}
			/>
		</PageLayout>
	);
};

RequestMonitoring.getLayout = (page: React.ReactElement) => {
	return <NavigationLayout>{page}</NavigationLayout>;
};

export const getStaticProps = getServerSideTranslationsWrapper(() => {
	return {
		props: {
			logged: true,
			mode: 'light',
		},
	};
});

export default RequestMonitoring;
